import * as React from 'react';
import { createContext, useContext, useEffect } from 'react';
import { useLocalStorageContext } from '@/contexts/LocalStorageContext';

type Theme = 'dark' | 'light' | 'pharmacy' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

// Create the context with a default value
const ThemeProviderContext = createContext<ThemeProviderState>({
  theme: 'system',
  setTheme: () => null,
});

// Ensure this is exported as a named export
export function ThemeProvider({
  children,
  ...props
}: ThemeProviderProps) {
  // Use theme from LocalStorageContext with error handling
  let storedTheme = 'system';
  let setStoredTheme = (theme: Theme) => { /* Theme provider fallback setter */ };

  try {
    const context = useLocalStorageContext();
    storedTheme = context.theme;
    setStoredTheme = context.setTheme;
  } catch (error) {
    // ThemeProvider: LocalStorageContext not available, using system theme as fallback
  }

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Clean up existing theme classes
    root.classList.remove('light', 'dark', 'pharmacy');

    const theme = storedTheme as Theme || 'system';

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
      
      // Add listener for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        root.classList.remove('light', 'dark');
        root.classList.add(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    root.classList.add(theme);
  }, [storedTheme]);

  const value = {
    theme: storedTheme as Theme || 'system',
    setTheme: (newTheme: Theme) => {
      setStoredTheme(newTheme);
    },
  };

  return (
    <ThemeProviderContext.Provider value={value} {...props}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};